import { useEffect, useRef, useState } from "react"
import { gql, useQuery } from "@apollo/client"
import { COACH_TOOLS_API } from "../../apolloClient"
import { isTerminated } from "../utils"

export const PROGRAM_TERMINATION_QUERY = gql`
  query ProgramTerminationQuery {
    currentPerson {
      id
      latestProgram {
        id
        status
        programType {
          code
        }
        band {
          id
          terminateCd
          terminatesOn
        }
      }
    }
  }
`

export const PROGRAM_TERMINATION_TIMEOUT_MS = 5000

// Legacy type codes match the V3 `termination.type` values `rules.js` reads.
// Prospect maps to QE because the program type does not distinguish PQ from QE.
const LEGACY_TYPE_BY_PROGRAM_TYPE = {
  ASSOCIATE: "AP",
  AFFILIATE: "AF",
  PROSPECT: "QE",
  MEMBER: "MB",
  STUDENT: "ST",
}

// `rules.js` keys on the live type and only maps AF to AP for the type stored on
// the termination, so Affiliates evaluate as AP.
const RULES_TYPE_BY_LEGACY_TYPE = {
  AP: "AP",
  AF: "AP",
  QE: "QE",
  MB: "MB",
  ST: "ST",
}

/**
 * Shape pga-backend's `latestProgram` like the V3 `termination` object so the
 * existing Terminated rules and modal keep working unchanged. Returns null
 * unless the program is TERMINATED (the status M360 shows).
 */
export const terminationFromLatestProgram = latestProgram => {
  if (latestProgram?.status !== "TERMINATED") return null

  return {
    status: "Terminated",
    type: LEGACY_TYPE_BY_PROGRAM_TYPE[latestProgram.programType?.code] || null,
    terminateCd: latestProgram.band?.terminateCd || null,
    terminateDate: latestProgram.band?.terminatesOn || null,
  }
}

/**
 * Fetches the program status. `error` also covers a reply slower than
 * PROGRAM_TERMINATION_TIMEOUT_MS so a hung request cannot hold the app on the
 * loading screen. `hasProgram` is false when pga-backend has no program for
 * the person, which callers treat as "unknown", not "not terminated".
 */
export const useProgramTermination = ({ skip = false } = {}) => {
  const { data, loading, error } = useQuery(PROGRAM_TERMINATION_QUERY, {
    context: { api: COACH_TOOLS_API },
    fetchPolicy: "network-only",
    skip,
  })
  const [timedOut, setTimedOut] = useState(false)
  const [latencyMs, setLatencyMs] = useState(null)
  const startedAt = useRef(null)
  const isLoading = !skip && loading

  useEffect(() => {
    if (skip) return
    if (startedAt.current === null) startedAt.current = Date.now()
    if (!isLoading && latencyMs === null) {
      setLatencyMs(Date.now() - startedAt.current)
    }
  }, [skip, isLoading, latencyMs])

  useEffect(() => {
    if (!isLoading) return undefined
    const timer = setTimeout(
      () => setTimedOut(true),
      PROGRAM_TERMINATION_TIMEOUT_MS,
    )
    return () => clearTimeout(timer)
  }, [isLoading])

  const latestProgram = data?.currentPerson?.latestProgram
  const failed = !skip && (error || timedOut)

  return {
    termination: terminationFromLatestProgram(latestProgram),
    hasProgram: !!latestProgram,
    timedOut: !skip && timedOut,
    latencyMs,
    loading: isLoading && !timedOut,
    error: failed
      ? error || new Error("Timed out loading program status")
      : undefined,
  }
}

/**
 * Decides who reaches the Terminated modal. Returns null when the person is
 * not terminated, otherwise `{ me, rulesMe }`:
 * - `me` goes to the modal and dashboard and keeps the V3 type fields, so the
 *   modal's reapply path and the dashboard shell behave as they do today.
 * - `rulesMe` goes to `isTerminatedRules`. With the program-status source on,
 *   it carries the pga-backend program type (Affiliate evaluates as AP), so a
 *   stale V3 type cannot pair with a pga-backend code (an Associate `GK` keeps
 *   access even when V3 still says Member).
 * A failed or slow query, or no program at all, falls back to the V3
 * `me.termination`.
 */
export const resolveTerminatedGate = ({
  me,
  useProgramStatus,
  programTermination,
  programError,
  hasProgram = true,
}) => {
  if (!me) return null
  if (useProgramStatus && !programError && hasProgram) {
    if (!programTermination) return null
    const termMe = { ...me, termination: programTermination }
    const rulesType = RULES_TYPE_BY_LEGACY_TYPE[programTermination.type]
    return {
      me: termMe,
      rulesMe: rulesType
        ? { ...termMe, type: rulesType, mainProgramTypeCode: rulesType }
        : termMe,
    }
  }
  return isTerminated(me) ? { me, rulesMe: me } : null
}

/** Why the program-status source fell back to V3, or null when it did not. */
export const programFallbackReason = ({
  useProgramStatus,
  loading,
  timedOut,
  error,
  hasProgram,
}) => {
  if (!useProgramStatus || loading) return null
  if (timedOut) return "timeout"
  if (error) return "error"
  if (!hasProgram) return "no_program"
  return null
}
